import {
  Badge,
  Button,
  FormDatePicker,
  FormInput,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { z } from "zod";
import { AlertCircleIcon, CheckIcon, FileTextIcon, RefreshCwIcon } from "lucide-react";
import { useState } from "react";
import { formatDateLocal } from "@/shared/lib/date-time";
import { SmartFileUpload } from "@/shared/ui/SmartFileUpload";

const RenewalFormSchema = z.object({
  newEndDate: z.date(),
  notes: z.string().optional(),
  renewalDocumentId: z.string().nullable().optional(),
});

export type RenewalFormValues = z.infer<typeof RenewalFormSchema>;

export function ContractRenewalForm({
  initialEndDate,
  contractTitle,
  onSubmit,
  submitting,
  onCancel,
}: {
  initialEndDate: string | Date;
  contractTitle?: string;
  onSubmit: (values: RenewalFormValues & { newEndDate: Date; renewalDocumentId?: string | null }) => void;
  submitting?: boolean;
  onCancel?: () => void;
}) {
  const [uploadedFileKey, setUploadedFileKey] = useState<string | null>(null);

  const currentEndDate = typeof initialEndDate === "string" ? new Date(initialEndDate) : initialEndDate;
  
  // Set default new end date to be 1 year after current end date
  const defaultNewEndDate = new Date(currentEndDate);
  defaultNewEndDate.setFullYear(defaultNewEndDate.getFullYear() + 1);

  const methods = RHF.useForm<RenewalFormValues>({
    resolver: zodResolver(RenewalFormSchema),
    defaultValues: {
      newEndDate: defaultNewEndDate,
      notes: "",
      renewalDocumentId: null,
    },
  });

  const handleFileUpload = (key: string) => {
    setUploadedFileKey(key);
    methods.setValue("renewalDocumentId", key);
  };

  const handleRemoveFile = () => {
    setUploadedFileKey(null);
    methods.setValue("renewalDocumentId", null);
  };

  const newEndDate = methods.watch("newEndDate");

  // Auto-validation: Check if new date is after current date
  const isValid = newEndDate && newEndDate instanceof Date && newEndDate > currentEndDate;

  // Get the current form value for defaultMonth
  const defaultMonthForPicker = newEndDate instanceof Date ? newEndDate : defaultNewEndDate;

  return (
    <>
      <FormRoot<RenewalFormValues>
        methods={methods}
        onSubmit={(vals) => {
          onSubmit({
            ...vals,
            newEndDate: vals.newEndDate,
            renewalDocumentId: vals.renewalDocumentId ?? null,
          });
        }}
        className="space-y-6"
      >
        {/* Contract Title */}
        {contractTitle && (
          <div className="mb-4 rounded-lg border bg-muted/50 p-3">
            <div className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              ສັນຍາ
            </div>
            <div className="mt-1 font-medium">{contractTitle}</div>
          </div>
        )}

        {/* Information Alert */}
        <div className="mb-4 rounded-lg border bg-muted/50 p-3">
          <div className="flex items-start gap-2">
            <div className="mt-0.5 flex-shrink-0">
              <FileTextIcon className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0 flex-1 text-sm">
              <div className="font-medium">ຂໍ້ມູນການຕໍ່ອາຍຸ</div>
              <div className="mt-1 break-words text-muted-foreground">
                ການຕໍ່ອາຍຸສັນຍານີ້ຈະສ້າງເວີຊັນໃໝ່ ແລະ ອັບເດດສະຖານະຂອງສັນຍາປັດຈຸບັນເມື່ອຢືນຢັນໂດຍການບັນຊີ.
              </div>
            </div>
          </div>
        </div>

        {/* Date Fields */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label htmlFor="currentEndDate" className="text-sm font-medium">ວັນທີຫມົດອາຍຸປັດຈຸບັນ</label>
            <div className="relative">
              <FormInput
                id="currentEndDate"
                name="currentEndDate"
                value={formatDateLocal(currentEndDate)}
                disabled
                className="pr-10"
              />
              <LockIcon className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>
          </div>

          <div className="space-y-2">
            <FormDatePicker
              name="newEndDate"
              label="ວັນທີຫມົດອາຍຸໃໝ່"
              requiredMark
              {...(defaultMonthForPicker && { defaultMonth: defaultMonthForPicker })}
            />
          </div>
        </div>

        {/* Upload Renewal Document */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">ອັບໂຫຼດເອກະສານຕໍ່ອາຍຸ</span>
            <span className="text-muted-foreground text-xs">ບໍ່ບັງຄັບ</span>
          </div>
          {uploadedFileKey ? (
            <div className="flex items-center gap-3 rounded-lg border bg-card p-4">
              <FileTextIcon className="h-8 w-8 text-primary" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {uploadedFileKey.split("/").pop() || "ເອກະສານ"}
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRemoveFile}
              >
                ລຶບ
              </Button>
            </div>
          ) : (
            <SmartFileUpload
              keyPrefix="uploads/contracts/renewal"
              accept=".pdf,.doc,.docx"
              onSuccess={handleFileUpload}
              buttonLabel="ອັບໂຫຼດເອກະສານຕໍ່ອາຍຸ"
              hint="PDF, DOCX ສູງສຸດ 10MB (ໄຟລ໌ໃຫຍ່ກວ່າ 5MB ຈະອັບໂຫຼດແບບ chunk)"
            />
          )}
        </div>

        {/* Renewal Notes */}
        <div className="space-y-2">
          <FormTextarea
            name="notes"
            placeholder="ອະທິບາຍການປ່ຽນແປງໃດໆ ກ່ຽວກັບລາຄາ, ຈຳນວນບ່ອນນັ່ງ, ຫຼື ເງື່ອນໄຂສຳລັບໄລຍະເວລາຕໍ່ອາຍຸນີ້..."
            rows={4}
            className="resize-none"
          />
        </div>

        {/* Validation Status */}
        <div className="flex items-center justify-between rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2">
            {isValid ? (
              <>
                <Badge variant="default" className="gap-1 bg-green-500 text-white">
                  <CheckIcon className="h-3 w-3" />
                  ການກວດສອບອັດຕະໂນມັດຜ່ານ
                </Badge>
              </>
            ) : (
              <Badge variant="secondary" className="gap-1">
                <AlertCircleIcon className="h-3 w-3" />
                ກະລຸນາເລືອກວັນທີຫມົດອາຍຸໃໝ່
              </Badge>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 border-t pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (onCancel) {
                onCancel();
              }
            }}
            disabled={submitting}
          >
            ຍົກເລີກ
          </Button>
          <Button type="submit" isLoading={submitting} disabled={!isValid}>
            <RefreshCwIcon className="mr-2 h-4 w-4" />
            ຢືນຢັນການຕໍ່ອາຍຸ
          </Button>
        </div>
      </FormRoot>

    </>
  );
}
